import { Component, OnInit, Renderer2, Inject, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TaskAnalysisModel } from 'src/app/models/task-analysis.model';
import { UtilsService } from 'src/app/shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { ApiRequestService } from '../../shared/api-request.service';
import { SiteModel } from '../../models/site.model';
import { RBACService } from '../../shared/rbac.service';

@Component({
    selector: 'app-task-analyses-edit',
    templateUrl: './task-analyses-edit.component.html',
    styleUrls: ['./task-analyses-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskAnalysesEditComponent implements OnInit {
  task_analysis: TaskAnalysisModel = new TaskAnalysisModel();

  dialogData: any;

  selectedFiles: File[] = [];

  inProgress: boolean = false;

  // Tracks whether any changes were saved (create/update/delete) so the list refreshes on close.
  hasChanges: boolean = false;

  // The RBAC feature constant for permission checks. Passed by the caller via dialog data.
  feature: string;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission checks on every change detection cycle. They are built
  // from the dynamic feature constant resolved in the constructor.
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public renderer: Renderer2,
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA)
    data: {
      sssp_id: number;
      task_analysis_id: number;
      site_id?: number;
      feature?: string;
    },
    private dialogRef: MatDialogRef<TaskAnalysesEditComponent>,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {
    // Default to F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS when feature is not provided (backward compatibility).
    this.feature = data.feature || RBACService.RBAC_F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS;

    if ( data.task_analysis_id ) {
      this.task_analysis.id = data.task_analysis_id;
    }

    if ( data.sssp_id ) {
      this.task_analysis.sssp_ids.push(data.sssp_id);
    }

    if ( data.site_id ) {
      this.task_analysis.site_ids = [data.site_id];
    }
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties.
   * This method checks if the current task analysis has an ID and, if so, calls the `find` method to perform further operations.
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    // Resolve the RBAC permission flags used by the template. The feature constant is
    // already resolved in the constructor, so it is safe to build the flags here.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(this.feature, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(this.feature, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(this.feature, RBACService.RBAC_A.A_DELETE)
    ], true);

    if ( this.task_analysis.id ) {
      this.find();
    }
  }

  /**
   * Fetches task analysis details from the API and applies the response data to the current task analysis object.
   * Also extracts and assigns site IDs from the associated sites.
   *
   * @return {void} This method does not return a value.
   */
  find(): void {
    this.api.makeRequest('get', `v1/task-analysis/${this.task_analysis.id}`).then((response: any): void => {
      this.task_analysis.apply(response.data);
      this.task_analysis.site_ids = this.task_analysis.sites.map((site: SiteModel): any => site.id);
    });
  }

  /**
   * Handles the submission of a form, performing validation, checking for ongoing processes,
   * and invoking appropriate create or update actions based on the state of the task analysis.
   *
   * @param {NgForm} form - The form object that is being submitted, containing user input data.
   * @return {void} No value is returned as this function primarily triggers side-effects.
   */
  onSubmit(form: NgForm): void {
    if ( !form.valid ) {
      this.utils.showModal('Validation Error', 'Please make sure you enter all fields with valid information.');
      return;
    }

    if ( this.inProgress ) {
      return;
    }

    this.inProgress = true;

    if ( this.task_analysis.id ) {
      this.update();
    } else {
      this.create();
    }
  }

  /**
   * Creates a new Task Analysis Document by making a POST request to the 'task-analysis' endpoint.
   * Handles the response to set the ID of the created document, displays a success message,
   * uploads any selected files, updates progress state, and navigates or closes the dialog as necessary.
   *
   * @return {void} This method does not return a value.
   */
  create(): void {
    this.api.makeRequest('post', 'v1/task-analysis', this.task_analysis).then((response: any): void => {
      this.task_analysis.id = response.data.id;
      const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
      this.utils.showToast(`The ${singular} was created.`);

      // Check if user has edit permission: if not, auto-close after create.
      const hasEditPermission = this.rbac.userCanAccess([
        RBACService.getPermission(this.feature, RBACService.RBAC_A.A_EDIT)
      ], true);

      const afterUpload = (): void => {
        this.inProgress = false;
        if ( this.dialogRef ) {
          this.dialogRef.close(true);
        } else {
          this.router.navigate(['/task-analysis']);
        }
      };

      if ( this.selectedFiles.length > 0 ) {
        this.uploadSelectedFiles(this.task_analysis.id, afterUpload);
      } else {
        afterUpload();
      }
    })
    .catch((): void => {
      this.inProgress = false;
    });
  }

  /**
   * Updates the task analysis document by sending a PUT request to the corresponding API endpoint.
   * Displays a toast notification upon successful update, and updates the document list.
   * Handles operation completion and error scenarios.
   *
   * @return {void} Does not return any value.
   */
  update(): void {
    this.api.makeRequest('put', 'v1/task-analysis/' + this.task_analysis.id, this.task_analysis).then((): void => {
      const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
      this.utils.showToast(`The ${singular} was updated.`);
      this.inProgress = false;
      this.hasChanges = true;
      this.find();
    })
    .catch((): void => {
      this.inProgress = false;
    });
  }

  /**
   * Handles the selection of files from the provided FileList object and adds them to the selected files list.
   *
   * @param {FileList} fileList - A list of files selected by the user.
   * @return {void} Does not return a value.
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Handles the upload of selected files for a specific task ID.
   * The method uploads the selected files and displays a success message upon completion.
   * An optional callback function can be executed after the upload process completes.
   *
   * @param {number} id - The identifier for the task associated with the upload.
   * @param {Function} [callback] - Optional callback function to execute after upload completion.
   * @return {void} This method does not return a value.
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/task_analysis/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    }
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.task_analysis.site_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (typeof results === 'undefined') { return; }
        this.task_analysis.site_ids = results;
      }
    );
  }

  onRemove() {
    const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove this ${singular}?`,
      () => {
        this.api.makeRequest(
          'delete',
          'v1/task-analysis/' + this.task_analysis.id
        )
        .then(() => {
          this.utils.showToast(`The ${singular} was removed.`);
          this.dialogRef.close(true);
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });
      }
    );
  }

  // Expose RBACService to template for inline permission checks.
  protected readonly RBACService = RBACService;
}
